import { reportVerifiedPurchase } from "../lib/metaPurchase";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

import {
  CheckoutStatus,
  completeMembershipSetup,
  getCheckoutStatus,
} from "../api/memberships";

type MembershipConfirmedPageProps = {
  onLogin: () => void;
};

function planName(interval: string): string {
  if (interval === "annual") return "annual";
  if (interval === "monthly") return "monthly";
  return "";
}

export default function MembershipConfirmedPage({
  onLogin,
}: MembershipConfirmedPageProps) {
  const sessionId = useMemo(
    () => new URLSearchParams(window.location.search).get("session_id")?.trim() || "",
    []
  );
  const [checkout, setCheckout] = useState<CheckoutStatus | null>(null);
  useEffect(() => {
    if (checkout && sessionId) reportVerifiedPurchase(sessionId);
  }, [checkout, sessionId]);
  const [checking, setChecking] = useState(true);
  const [checkError, setCheckError] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [marketingEmailsOptedOut, setMarketingEmailsOptedOut] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [complete, setComplete] = useState(false);
  const pendingCheck = useRef<Promise<CheckoutStatus> | null>(null);
  const checkGeneration = useRef(0);

  const checkMembership = useCallback(async () => {
    if (!sessionId) {
      setCheckError(
        "This page needs the secure reference supplied after payment. Please use the link from your Stripe confirmation."
      );
      setChecking(false);
      return;
    }

    const generation = ++checkGeneration.current;
    setChecking(true);
    setCheckError("");
    // React StrictMode may run the initial effect twice. Share that request
    // instead of creating the same membership concurrently on the backend.
    const request = pendingCheck.current || getCheckoutStatus(sessionId);
    pendingCheck.current = request;
    try {
      const result = await request;
      if (generation !== checkGeneration.current) return;
      setCheckout(result);
      setCheckError("");
    } catch (error) {
      if (generation !== checkGeneration.current) return;
      setCheckout(null);
      setCheckError(
        error instanceof Error
          ? error.message
          : "We could not confirm this membership payment. Please try again."
      );
    } finally {
      if (pendingCheck.current === request) pendingCheck.current = null;
      if (generation === checkGeneration.current) setChecking(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void checkMembership();
    return () => { checkGeneration.current += 1; };
  }, [checkMembership]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError("");

    if (password.length < 10) {
      setSubmitError("Please choose a password containing at least 10 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError("The two passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await completeMembershipSetup(
        sessionId,
        password,
        displayName,
        marketingEmailsOptedOut
      );
      window.history.replaceState({}, "", "/membership-confirmed");
      setComplete(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "We could not finish setting up your account. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  const selectedPlan = checkout ? planName(checkout.plan_interval) : "";

  return (
    <main className="entry-container membership-confirmed-page">
      <Helmet>
        <title>Complete Your Sanctuary Account | Calm But Capable</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>

      <header className="entry-header">
        <a className="entry-brand membership-brand-link" href="/">
          <img src="/logo-white.png" alt="" className="entry-logo-small" />
          <span>
            <span className="entry-brand-name">Calm But Capable</span>
            <span className="entry-brand-subtitle">The Sanctuary</span>
          </span>
        </a>
      </header>

      <section className="entry-card membership-setup-card" aria-live="polite">
        {checking && (
          <>
            <p className="entry-eyebrow">One moment</p>
            <h1 className="membership-setup-title">Confirming your membership</h1>
            <p className="entry-subtitle membership-copy">
              We are securely checking the payment before creating your account.
            </p>
          </>
        )}

        {!checking && checkError && (
          <>
            <p className="entry-eyebrow">We could not confirm that yet</p>
            <h1 className="membership-setup-title">Your payment has not been lost</h1>
            <p className="entry-subtitle membership-copy">{checkError}</p>
            {sessionId && (
              <button className="entry-button membership-action" onClick={checkMembership}>
                Try confirming again
              </button>
            )}
            <p className="entry-small-print membership-support-copy">
              If this continues, contact hello@calmbutcapable.com and we will help.
            </p>
          </>
        )}

        {!checking && !checkError && checkout && !checkout.setup_required && (
          <>
            <p className="entry-eyebrow">Payment confirmed</p>
            <h1 className="membership-setup-title">Your account is ready</h1>
            <p className="entry-subtitle membership-copy">
              A Sanctuary account already exists for {checkout.email_hint}. You can log in now.
            </p>
            <button className="entry-button membership-action" onClick={onLogin}>
              Continue to login
            </button>
          </>
        )}

        {!checking && !checkError && checkout?.setup_required && !complete && (
          <>
            <p className="entry-eyebrow">Payment confirmed</p>
            <h1 className="membership-setup-title">Welcome to The Sanctuary</h1>
            <p className="entry-subtitle membership-copy">
              Your{selectedPlan ? ` ${selectedPlan}` : ""} membership for {checkout.email_hint} is ready.
              Choose a password to finish creating your account.
            </p>

            <form className="membership-setup-form" onSubmit={handleSubmit}>
              <label htmlFor="membership-display-name">What would you like us to call you?</label>
              <input
                id="membership-display-name"
                className="entry-input membership-input"
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={120}
                autoComplete="name"
                placeholder="Your name (optional)"
              />

              <label htmlFor="membership-password">Create your password</label>
              <input
                id="membership-password"
                className="entry-input membership-input"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={10}
                maxLength={72}
                autoComplete="new-password"
                required
              />
              <p className="membership-field-help">Use at least 10 characters.</p>

              <label htmlFor="membership-confirm-password">Confirm your password</label>
              <input
                id="membership-confirm-password"
                className="entry-input membership-input"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={10}
                maxLength={72}
                autoComplete="new-password"
                required
              />

              <fieldset className="membership-email-choice">
                <legend>Email communication</legend>
                <p>
                  As part of your Sanctuary membership, Calm But Capable will
                  email you important information about your account, payments
                  and access.
                </p>
                <p>
                  We would also like to send practical guidance, useful
                  resources and information about similar Calm But Capable
                  services. You can unsubscribe from these additional emails
                  at any time.
                </p>
                <label className="membership-email-opt-out">
                  <input
                    type="checkbox"
                    checked={marketingEmailsOptedOut}
                    onChange={(event) =>
                      setMarketingEmailsOptedOut(event.target.checked)
                    }
                  />
                  <span>
                    I do not want to receive additional guidance and updates.
                  </span>
                </label>
              </fieldset>

              {submitError && (
                <p className="membership-form-error" role="alert">
                  {submitError}
                </p>
              )}

              <button className="entry-button" type="submit" disabled={submitting}>
                {submitting ? "Creating your account…" : "Create my Sanctuary account"}
              </button>
            </form>

            <p className="entry-small-print membership-support-copy">
              Your email comes securely from the completed payment and cannot be changed here.
            </p>
          </>
        )}

        {complete && (
          <>
            <p className="entry-eyebrow">Account created</p>
            <h1 className="membership-setup-title">You are ready to begin</h1>
            <p className="entry-subtitle membership-copy">
              Your Sanctuary account is secure and ready. Log in using the email address from your payment and the password you just created.
            </p>
            <button className="entry-button membership-action" onClick={onLogin}>
              Continue to login
            </button>
          </>
        )}
      </section>
    </main>
  );
}
